export type RoyalChallengeValueFormat = 'number' | 'centimeters' | 'minutes' | 'cents';

export interface RoyalChallengeHallTrialConfig {
  readonly id: string;
  readonly skillId: string;
  readonly crestName: string;
  readonly prompt: string;
  readonly answer: number;
  readonly initialValue: number;
  readonly minimum: number;
  readonly maximum: number;
  readonly stepSizes: readonly number[];
  readonly format: RoyalChallengeValueFormat;
  readonly hints: readonly [string, string];
}

export function validateRoyalChallengeHallTrial<T extends RoyalChallengeHallTrialConfig>(config: T) {
  if (!config.id.trim() || !config.skillId.trim() || !config.crestName.trim() || !config.prompt.trim()) {
    throw new RangeError('Royal Challenge Hall identifiers and copy must not be empty.');
  }
  if (!Number.isInteger(config.answer) || config.answer < config.minimum || config.answer > config.maximum) {
    throw new RangeError(`${config.id} answer must be an integer within its dial range.`);
  }
  if (!Number.isInteger(config.initialValue) || config.initialValue < config.minimum || config.initialValue > config.maximum) {
    throw new RangeError(`${config.id} initial value must be an integer within its dial range.`);
  }
  if (config.stepSizes.length < 1 || config.stepSizes.some(step => !Number.isInteger(step) || step < 1)) {
    throw new RangeError(`${config.id} requires positive integer tuning steps.`);
  }
  return config;
}

export const ROYAL_CHALLENGE_HALL_TRIALS = [
  validateRoyalChallengeHallTrial({
    id: 'royal-hall-place-value', skillId: 'math.place-value.compose-hundreds',
    crestName: 'PLACE VALUE CREST',
    prompt: 'The first seal holds 4 hundreds, 6 tens, and 3 ones. Tune the crest to that number.',
    answer: 463, initialValue: 400, minimum: 0, maximum: 999, stepSizes: [100, 10, 1], format: 'number',
    hints: ['Build the hundreds first, then tens, then ones.', 'Start at 400. Add 60, then add 3.'],
  }),
  validateRoyalChallengeHallTrial({
    id: 'royal-hall-addition', skillId: 'math.addition.within-100-strategies',
    crestName: 'ADDITION CREST',
    prompt: 'The Forge sends 48 blue sparks and 37 gold sparks. Tune the crest to the total.',
    answer: 85, initialValue: 48, minimum: 0, maximum: 100, stepSizes: [10, 1], format: 'number',
    hints: ['Add the tens and ones in parts.', '48 + 30 = 78, then add 7.'],
  }),
  validateRoyalChallengeHallTrial({
    id: 'royal-hall-subtraction', skillId: 'math.subtraction.within-100-regrouping',
    crestName: 'SUBTRACTION CREST',
    prompt: 'The Keep had 72 keys. Twenty-eight opened gates. Tune the crest to the keys left.',
    answer: 44, initialValue: 72, minimum: 0, maximum: 100, stepSizes: [10, 1], format: 'number',
    hints: ['Subtract 20, then subtract 8.', '72 - 20 = 52. Eight fewer is 44.'],
  }),
  validateRoyalChallengeHallTrial({
    id: 'royal-hall-measurement', skillId: 'math.measurement.add-lengths',
    crestName: 'MEASUREMENT CREST',
    prompt: 'Market ribbons measure 18 cm, 27 cm, and 35 cm. Tune the crest to their total length.',
    answer: 80, initialValue: 50, minimum: 0, maximum: 150, stepSizes: [10, 1], format: 'centimeters',
    hints: ['Combine 18 and 27 first, then add 35.', '18 + 27 = 45. Then 45 + 35 makes a new ten.'],
  }),
  validateRoyalChallengeHallTrial({
    id: 'royal-hall-time', skillId: 'math.time.elapsed-five-minute-intervals',
    crestName: 'TIME CREST',
    prompt: 'The parade begins at 2:20 and reaches the hall at 2:55. Tune the elapsed time.',
    answer: 35, initialValue: 20, minimum: 0, maximum: 60, stepSizes: [10, 5], format: 'minutes',
    hints: ['Count forward from 2:20 in five-minute jumps.', 'There are seven five-minute jumps from :20 to :55.'],
  }),
  validateRoyalChallengeHallTrial({
    id: 'royal-hall-coins', skillId: 'math.money.coin-combinations',
    crestName: 'COIN CREST',
    prompt: 'Two 25¢ coins, one 10¢ coin, one 5¢ coin, and three 1¢ coins power the final seal. Tune the total.',
    answer: 68, initialValue: 50, minimum: 0, maximum: 100, stepSizes: [10, 5, 1], format: 'cents',
    hints: ['Combine the two largest coins before adding the smaller values.', '50 + 10 + 5 + 3 = the final amount.'],
  }),
] as const satisfies readonly RoyalChallengeHallTrialConfig[];

export const ROYAL_CHALLENGE_HALL_COMPLETION_EVENT = 'number-kingdom-royal-hall-restored';

export function formatRoyalChallengeValue(value: number, format: RoyalChallengeValueFormat) {
  if (format === 'centimeters') return `${value} cm`;
  if (format === 'minutes') return `${value} minutes`;
  if (format === 'cents') return `${value}¢`;
  return `${value}`;
}
